import { useEffect, useEffectEvent } from 'react';
import { useNavigate } from 'react-router';
import { defaultKeybindingsHandlerIgnore, tinykeys } from 'tinykeys';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { BOARD_COLUMN_CONTENT_SELECTOR } from '~/ui/core/focus/focus-regions';
import { useKeyboardShortcutsEnabled } from '~/ui/core/keyboard-shortcuts/KeyboardShortcutsContext';

import { useNavigationMenu } from './NavigationMenuContext';
import type { NavigationItem } from './navigation-items';

export const CHORD_TIMEOUT_MS = 1500;

const ownsLetterKeys = (event: KeyboardEvent): boolean =>
    event.target instanceof HTMLElement &&
    event.target.closest(
        `[role="menu"], [role="menubar"], [role="listbox"], ${BOARD_COLUMN_CONTENT_SELECTOR}, [role="combobox"], [role="dialog"]`,
    ) !== null;

export const ignoreKeyboardShortcut = (event: KeyboardEvent): boolean =>
    event.isComposing || ownsLetterKeys(event) || defaultKeybindingsHandlerIgnore(event);

type Navigate = (path: string) => void;
type OpenExternalLink = (path: string) => void;

export const createNavigationKeybindings = (
    items: readonly NavigationItem[],
    navigate: Navigate,
    openExternalLink: OpenExternalLink,
): Record<string, (event: KeyboardEvent) => void> => {
    const keybindings: Record<string, (event: KeyboardEvent) => void> = {};

    for (const { externalUrl, path, shortcutKey } of items) {
        if (shortcutKey === undefined) {
            continue;
        }

        const chord = `g ${shortcutKey}`;

        if (chord in keybindings) {
            throw new Error(`Duplicate navigation shortcut: ${chord}`);
        }

        keybindings[chord] = (event) => {
            if (event.defaultPrevented) {
                return;
            }

            event.preventDefault();

            if (externalUrl) {
                openExternalLink(path);
            } else {
                navigate(path);
            }
        };
    }

    return keybindings;
};

export const useAreNavigationShortcutsEnabled = (): boolean => {
    const {
        applicationState: { featureFlagService },
    } = useApplicationContext();
    const keyboardShortcutsEnabled = useKeyboardShortcutsEnabled();

    return featureFlagService.isFeatureFlagEnabled('keyboard-shortcuts') && keyboardShortcutsEnabled;
};

export const useNavigationShortcuts = (items: readonly NavigationItem[], openChat?: () => void): void => {
    const enabled = useAreNavigationShortcutsEnabled();
    const { toggleMenu } = useNavigationMenu();
    const navigate = useNavigate();
    const {
        actions: { openWindow },
    } = useApplicationContext();

    // Items are rebuilt on every render and paths fill in as data loads; rebinding tinykeys then would drop a chord
    // the user is halfway through, so it binds per set of keys and resolves the current item when the chord fires.
    const runChord = useEffectEvent((chord: string, event: KeyboardEvent) => {
        if (chord === 'b' || chord === 'c') {
            if (!event.defaultPrevented) {
                event.preventDefault();
                if (chord === 'b') {
                    toggleMenu();
                } else {
                    openChat?.();
                }
            }

            return;
        }

        createNavigationKeybindings(items, navigate, openWindow)[chord]?.(event);
    });
    const chords = [
        ...Object.keys(createNavigationKeybindings(items, navigate, openWindow)),
        // Page-level chords must finish without triggering their final key's panel action.
        'n c',
        'v b',
        'b',
        ...(openChat ? ['c'] : []),
    ].join(',');

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const keybindings = Object.fromEntries(
            chords.split(',').map((chord) => [chord, (event: KeyboardEvent) => runChord(chord, event)]),
        );

        return tinykeys(window, keybindings, { ignore: ignoreKeyboardShortcut, timeout: CHORD_TIMEOUT_MS });
    }, [enabled, chords]);
};
